import {
  OAUTH_CONNECTION_DEFAULT_EXPIRY_DAYS,
  OAUTH_CONNECTION_EXPIRY_DAYS,
  type OauthAuthorizeParams,
  type OauthConsentInfo,
} from '@fos/shared/contracts/oauth';
import type { ApiTokenAccess } from '@fos/shared/enums';
import { API_TOKEN_ACCESS_DESCRIPTIONS, API_TOKEN_ACCESS_LABELS, tokenExpiryLabel } from '@fos/shared/labels';
import { type FormEvent, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMyWorkspaces } from '../../../app/layout/other-workspaces';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useSession, useSwitchWorkspace } from '../../../lib/auth';
import { trpc } from '../../../lib/trpc';
import { ContextBanner, FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Combobox } from '../../../ui/Combobox';
import { Field, Label } from '../../../ui/Field';
import { LogoLockup } from '../../../ui/Logo';
import { Mono } from '../../../ui/Mono';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { Spinner } from '../../../ui/Spinner';
import { toast } from '../../../ui/Toast';
import { PickedList } from './NewTokenDialog';

/** Parametry żądania autoryzacji z adresu (`/polacz?client_id=…&redirect_uri=…&code_challenge=…`). */
const PARAM_KEYS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
  'scope',
  'resource',
] as const satisfies readonly (keyof OauthAuthorizeParams)[];

type ScopeMode = 'all' | 'selected';
const SCOPE_OPTIONS = [
  { value: 'all' as const, label: 'Wszystko' },
  { value: 'selected' as const, label: 'Wybrani klienci i projekty' },
];
const EXPIRY_OPTIONS = OAUTH_CONNECTION_EXPIRY_DAYS.map((d) => ({ value: String(d), label: tokenExpiryLabel(d) }));

/**
 * Ekran zgody konektora (OAuth 2.1 dla MCP; docs/MCP_TOOLS.md §4.3): Claude (claude.ai, aplikacja na telefonie,
 * Desktop) prosi o dostęp do Fravity OS. Osoba jest zalogowana (strażnik tras: bez sesji → logowanie z 2FA → powrót
 * tutaj), widzi nazwę aplikacji i host adresu powrotu, wybiera workspace (przełączenie sesji), poziom, zakres
 * i ważność — jak w „Nowy token”. „Zezwól” wymaga potwierdzenia hasłem (step-up); „Odmów” wraca do aplikacji z
 * `access_denied`. Przekierowanie robi przeglądarka na adres zwrócony przez serwer (tylko zarejestrowany adres powrotu).
 */
export function ConnectorConsentPage() {
  const [searchParams] = useSearchParams();
  const request = useMemo(() => {
    const r: OauthAuthorizeParams = {};
    for (const k of PARAM_KEYS) {
      const v = searchParams.get(k);
      if (v !== null) r[k] = v;
    }
    return r;
  }, [searchParams]);
  const session = useSession();
  const activeWorkspaceId = session.data?.authenticated ? (session.data.workspace?.id ?? '') : '';
  const info = trpc.oauth.consentInfo.useQuery({ request }, { retry: false, staleTime: Number.POSITIVE_INFINITY });

  return (
    <div className="flex min-h-dvh flex-col items-center bg-app px-4 pt-12 pb-10 md:pt-[max(48px,12vh)]">
      <main className="w-full max-w-[520px]">
        <div className="mb-6 flex justify-center">
          <LogoLockup />
        </div>
        {info.isPending ? (
          <div className="flex justify-center py-10" role="status" aria-label="Wczytywanie">
            <Spinner size={18} className="text-fg-muted" />
          </div>
        ) : info.isError ? (
          <section className="rounded-2xl border border-line bg-panel px-6 py-7">
            <h1 className="text-h4 text-fg">Nie udało się połączyć aplikacji</h1>
            <p className="mt-2 text-body-s text-fg-secondary">{errorMessage(info.error)}</p>
            <div className="mt-5">
              <Button variant="secondary" onClick={() => window.location.assign('/dzis')}>
                Wróć do Fravity OS
              </Button>
            </div>
          </section>
        ) : (
          <ConsentForm
            key={activeWorkspaceId}
            request={request}
            info={info.data}
            activeWorkspaceId={activeWorkspaceId}
          />
        )}
      </main>
    </div>
  );
}

function ConsentForm({
  request,
  info,
  activeWorkspaceId,
}: {
  request: OauthAuthorizeParams;
  info: OauthConsentInfo;
  activeWorkspaceId: string;
}) {
  const approve = trpc.oauth.approve.useMutation();
  const deny = trpc.oauth.deny.useMutation();
  const workspaces = useMyWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const [access, setAccess] = useState<ApiTokenAccess>(info.maxAccess);
  const [scope, setScope] = useState<ScopeMode>('all');
  const [clientIds, setClientIds] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [expiry, setExpiry] = useState(String(OAUTH_CONNECTION_DEFAULT_EXPIRY_DAYS));
  const [error, setError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const options = trpc.tokens.scopeOptions.useQuery(undefined, { enabled: scope === 'selected', staleTime: 60_000 });

  const appName = info.client.kind === 'claude' ? 'Claude' : info.client.name || 'Aplikacja';
  const accessOptions = (info.maxAccess === 'write' ? (['read', 'write'] as const) : (['read'] as const)).map((v) => ({
    value: v,
    label: API_TOKEN_ACCESS_LABELS[v],
  }));
  const clients = options.data?.clients ?? [];
  const projects = options.data?.projects ?? [];
  const workspaceOptions = (workspaces.data ?? []).map((w) => ({ value: w.id, label: w.name }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setScopeError(null);
    if (scope === 'selected' && clientIds.length + projectIds.length === 0) {
      setScopeError('Wybierz co najmniej jednego klienta albo projekt');
      return;
    }
    try {
      const res = await approve.mutateAsync({
        request,
        access,
        scope: scope === 'all' ? { all: true } : { all: false, clientIds, projectIds },
        expiresInDays: Number(expiry),
      });
      setLeaving(true);
      window.location.assign(res.redirectTo);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return; // anulowane potwierdzenie hasłem
      const scopeIssue = validationIssues(err).find((i) => i.path.startsWith('scope'));
      if (scopeIssue) setScopeError(scopeIssue.message);
      else setError(errorMessage(err));
    }
  };

  const refuse = async () => {
    setError(null);
    try {
      const res = await deny.mutateAsync({ request });
      setLeaving(true);
      window.location.assign(res.redirectTo);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <form onSubmit={submit} noValidate aria-label="Zgoda na połączenie" className="flex flex-col gap-3">
      <section className="rounded-2xl border border-line bg-panel px-6 py-6">
        <h1 className="text-h4 text-fg">{`${appName} prosi o dostęp do Fravity OS`}</h1>
        <p className="mt-1.5 text-body-s text-fg-secondary">
          Po zgodzie {appName} będzie działać z Twoimi uprawnieniami, zawężonymi do wybranego zakresu. Maile, faktury i
          wdrożenia i tak przejdą przez Akceptacje. Haseł z sejfu nie zobaczy.
        </p>
        <dl className="mt-4 grid grid-cols-[120px_1fr] gap-x-4 gap-y-2 text-body-s">
          <dt className="text-fg-muted">Aplikacja</dt>
          <dd className="min-w-0 text-fg">{info.client.name || appName}</dd>
          <dt className="text-fg-muted">Adres powrotu</dt>
          <dd className="min-w-0 text-fg">
            <Mono>{info.client.redirectHost}</Mono>
          </dd>
        </dl>
        {info.client.kind === 'other' && (
          <ContextBanner tone="warning" className="mt-4">
            To nie jest adres Claude. Zezwól tylko, jeśli znasz tę aplikację i sam ją teraz łączysz.
          </ContextBanner>
        )}
        {info.client.kind === 'local' && (
          <ContextBanner tone="info" className="mt-4">
            Aplikacja na tym komputerze (np. Claude Code). Zezwól tylko, jeśli to Ty ją teraz łączysz.
          </ContextBanner>
        )}
      </section>

      <section className="flex flex-col gap-5 rounded-2xl border border-line bg-panel px-6 py-6">
        {workspaceOptions.length > 1 && (
          <Field label="Workspace" hint="Konektor działa w jednym workspace’ie. Zmiana przełącza też aplikację.">
            <Select
              aria-label="Workspace"
              value={activeWorkspaceId}
              options={workspaceOptions}
              disabled={switchWorkspace.isPending}
              onValueChange={(id) => {
                if (id === activeWorkspaceId) return;
                switchWorkspace.mutateAsync(id).catch((err: unknown) => toast.error(errorMessage(err)));
              }}
            />
          </Field>
        )}

        <div className="flex flex-col gap-2">
          <Label>Poziom</Label>
          <Segmented
            aria-label="Poziom"
            value={access}
            onValueChange={setAccess}
            options={accessOptions}
            className="self-start"
          />
          <p className="text-caption text-fg-muted">{API_TOKEN_ACCESS_DESCRIPTIONS[access]}</p>
        </div>

        <div className="flex flex-col gap-2">
          <Label>Zakres</Label>
          <Segmented
            aria-label="Zakres"
            value={scope}
            onValueChange={setScope}
            options={SCOPE_OPTIONS}
            className="self-start"
          />
          {scope === 'all' ? (
            <p className="text-caption text-fg-muted">
              Wszystko, do czego masz dostęp — także Twoje zadania wewnętrzne.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <PickedList
                label="Klienci"
                items={clientIds.map((id) => ({ id, label: clients.find((c) => c.id === id)?.name ?? '…' }))}
                onRemove={(id) => setClientIds((xs) => xs.filter((x) => x !== id))}
              />
              <Combobox
                aria-label="Dodaj klienta"
                value={null}
                placeholder={options.isPending ? 'Wczytywanie…' : 'Dodaj klienta'}
                searchPlaceholder="Szukaj klienta…"
                emptyText="Brak klientów"
                options={clients.filter((c) => !clientIds.includes(c.id)).map((c) => ({ value: c.id, label: c.name }))}
                disabled={options.isPending}
                onValueChange={(id) => setClientIds((xs) => [...xs, id])}
              />
              <PickedList
                label="Projekty"
                items={projectIds.map((id) => {
                  const p = projects.find((x) => x.id === id);
                  return { id, label: p ? (p.clientName ? `${p.name} · ${p.clientName}` : p.name) : '…' };
                })}
                onRemove={(id) => setProjectIds((xs) => xs.filter((x) => x !== id))}
              />
              <Combobox
                aria-label="Dodaj projekt"
                value={null}
                placeholder={options.isPending ? 'Wczytywanie…' : 'Dodaj projekt'}
                searchPlaceholder="Szukaj projektu…"
                emptyText="Brak projektów"
                options={projects
                  .filter((p) => !projectIds.includes(p.id))
                  .map((p) => ({ value: p.id, label: p.name, ...(p.clientName ? { description: p.clientName } : {}) }))}
                disabled={options.isPending}
                onValueChange={(id) => setProjectIds((xs) => [...xs, id])}
              />
              {scopeError && (
                <p role="alert" className="text-caption text-danger">
                  {scopeError}
                </p>
              )}
            </div>
          )}
        </div>

        <Field
          label="Ważność dostępu"
          hint="Potem Claude poprosi o ponowne połączenie. Odłączysz go wcześniej w Ustawieniach."
        >
          <Select aria-label="Ważność dostępu" value={expiry} onValueChange={setExpiry} options={EXPIRY_OPTIONS} />
        </Field>
      </section>

      {error && <FormMessage>{error}</FormMessage>}
      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={() => void refuse()} loading={deny.isPending} disabled={leaving}>
          Odmów
        </Button>
        <Button variant="primary" type="submit" loading={approve.isPending || leaving}>
          Zezwól
        </Button>
      </div>
      <p className="text-center text-caption text-fg-muted">
        Połączenie zobaczysz i odłączysz w Ustawienia → Tokeny i agenci.
      </p>
    </form>
  );
}
